import { Component, OnInit, OnDestroy } from '@angular/core';
import { faPenToSquare } from '@fortawesome/free-regular-svg-icons';
import { faCircle, faDownload, faSort, faSortDown, faSortUp, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Subject, Subscription, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil, catchError } from 'rxjs/operators';
import { ITemplate } from 'src/app/models/template.model';
import { DownloadService } from 'src/app/services/download.service';
import { Router } from '@angular/router';
import { TemplatesService } from 'src/app/services/templates.service';
import { SearchService } from '../../services/search.service';

const TEMPLATES_FILTERS_KEY = 'templatesListFilters';

interface TemplatesListFilters {
  searchTerm?: string;
  sortBy: string;
  orderBy: string;
  currentPage: number;
  pageSize: number;
}

@Component({
  selector: 'app-templates-v2',
  templateUrl: './templates.component.html',
  styleUrls: ['./templates.component.sass']
})
export class TemplatesV2Component implements OnInit, OnDestroy {
  faCircle = faCircle;
  faDownload = faDownload;
  faPenToSquare = faPenToSquare;
  faSort = faSort;
  faSortUp = faSortUp;
  faSortDown = faSortDown;
  faDelete = faTrash;
  faTimes = faTimes;

  // Expor Math para o template
  Math = Math;

  loading = false;
  showConfirmDeletePopup = false;
  templateToDelete: string = '';

  templates: ITemplate[] = [];
  current_page: number = 0;
  total_pages: number = 0;
  total_elements: number = 0;
  elements_per_page: number = 0;
  pageSize = 10;
  sortBy = 'ASC';
  orderBy = 'name';

  searchTerm: string | undefined = undefined;
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  private searchSubscription?: Subscription;

  constructor(
    private templatesService: TemplatesService,
    private router: Router,
    private downloadService: DownloadService,
    private searchService: SearchService
  ) {}

  ngOnInit(): void {
    // Restaura os filtros salvos (se existirem)
    this.loadSavedFilters();

    this.getTemplates(this.current_page, this.pageSize);

    // Se não há filtro restaurado, limpa o termo de busca
    // (para não manter busca de outras páginas)
    if (!this.searchTerm) {
      this.searchService.setSearchTerm('');
    }

    // Escuta mudanças no termo de pesquisa do header (ícone de lupa)
    this.searchSubscription = this.searchService.searchTerm$.subscribe((term: string) => {
      if (this.searchTerm !== (term || undefined)) {
        this.searchTerm = term || undefined;
        this.getTemplates(0, this.pageSize);
      }
    });

    // Debounced search pipeline: debounce, ignore equals, cancel previous HTTP via switchMap
    this.searchSubject.pipe(
      debounceTime(1000),
      distinctUntilChanged(),
      switchMap((term: string) => {
        const normalized = term === '' ? undefined : term;
        this.searchTerm = normalized;
        this.loading = true;
        return this.templatesService.listPaged(0, this.pageSize, this.sortBy, this.orderBy, this.searchTerm || '').pipe(
          catchError((err) => {
            console.error('Error searching templates (v2)', err);
            this.loading = false;
            return of(null);
          })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe((templates) => {
      if (!templates) return;
      this.templates = templates.data;
      this.current_page = templates.page_info.current_page;
      this.total_pages = templates.page_info.total_pages;
      this.total_elements = templates.page_info.total_elements;
      this.elements_per_page = templates.page_info.elements_per_page;
      this.loading = false;
    });
  }

  edit(id?: string) {
    // Salva os filtros antes de navegar para a edição
    this.saveFiltersToLocalStorage();
    this.router.navigate(['main/v2/templates', id, 'edit']);
  }

  showDeleteModal(id?: string) {
    this.showConfirmDeletePopup = true;
    this.templateToDelete = id || '';
  }

  closeDeleteModal() {
    this.showConfirmDeletePopup = false;
    this.templateToDelete = '';
  }

  onDelete() {
    this.loading = true;
    this.showConfirmDeletePopup = false;
    this.templatesService.deleteTemplate(this.templateToDelete)
      .pipe(
        catchError(error => {
          this.templateToDelete = '';
          this.loading = false;
          return error;
        })
      )
      .subscribe(() => {
        this.getTemplates(this.current_page, this.pageSize);
        this.templateToDelete = '';
      });
  }

  changeOrdernation(column: string) {
    if (this.orderBy === column)
      this.sortBy = this.sortBy === 'ASC' ? 'DESC' : 'ASC';
    else {
      this.orderBy = column;
      this.sortBy = 'ASC';
    }
    this.getTemplates(this.current_page, this.pageSize);
  }

  getTemplates(pageNumber: number, pageSize: number) {
    this.loading = true;
    return new Promise((resolve) => {
      this.templatesService.listPaged(pageNumber, pageSize, this.sortBy, this.orderBy, this.searchTerm || '')
        .subscribe((templates) => {
          this.templates = templates.data;
          this.current_page = templates.page_info.current_page;
          this.total_pages = templates.page_info.total_pages;
          this.total_elements = templates.page_info.total_elements;
          this.elements_per_page = templates.page_info.elements_per_page;
          this.loading = false;
        });
    });
  }

  navigateTo(page: number | string) {
    if (page !== '...')
      this.getTemplates(+page - 1, this.pageSize);
  }

  changePageSize(event: Event) {
    this.pageSize = +(<HTMLSelectElement>event.target).value;
    this.getTemplates(0, this.pageSize);
  }

  previous() {
    if (this.current_page > 0)
      this.getTemplates(this.current_page - 1, this.pageSize);
  }

  next() {
    if (this.current_page + 1 < this.total_pages)
      this.getTemplates(this.current_page + 1, this.pageSize);
  }

  generatePagination(current: number, total: number): (string | number)[] {
    const range = (start: number, end: number): number[] => {
      return Array.from({ length: (end - start) }, (v, k) => k + start);
    };
    let pages: (string | number)[] = [1];
    let left = current - 1;
    let right = current + 1;
    if (left < 2) right += 2 - left;
    if (right > total - 1) left -= right - (total - 1);
    left = Math.max(2, left);
    right = Math.min(total - 1, right);
    if (left > 2) pages.push('...');
    pages = pages.concat(range(left, right + 1));
    if (right < total - 1) pages.push('...');
    if (total > 1 && total !== pages[pages.length - 1]) pages.push(total);
    return pages;
  }

  create() {
    this.router.navigate(['main/v2/templates', 'create']);
  }

  searchTemplate(search: string) {
    // Emit to debounced pipeline; switchMap cancels previous HTTP requests
    this.searchSubject.next(search);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchSubject.complete();
    this.searchSubscription?.unsubscribe();
  }

  downloadTemplate(template: ITemplate) {
    if (!template.url_link) {
      console.error('URL do template não encontrada');
      return;
    }
    const url = template.url_link.toLowerCase();
    let fileType = 'docx';
    if (url.includes('.pdf')) {
      fileType = 'pdf';
    } else if (url.includes('.pptx')) {
      fileType = 'pptx';
    }
    const fileName = `${template.name}_${new Date().getTime()}.${fileType}`;
    this.downloadService.downloadTemplate(template.url_link, fileName, fileType);
  }

  // ========== FILTROS - LOCALSTORAGE ==========

  /**
   * Salva os filtros atuais no localStorage
   */
  private saveFiltersToLocalStorage(): void {
    try {
      const filters: TemplatesListFilters = {
        searchTerm: this.searchTerm,
        sortBy: this.sortBy,
        orderBy: this.orderBy,
        currentPage: this.current_page,
        pageSize: this.pageSize
      };
      localStorage.setItem(TEMPLATES_FILTERS_KEY, JSON.stringify(filters));
    } catch (e) {
      console.warn('Erro ao salvar filtros de templates:', e);
    }
  }

  /**
   * Carrega os filtros salvos do localStorage
   */
  private loadSavedFilters(): void {
    try {
      const saved = localStorage.getItem(TEMPLATES_FILTERS_KEY);
      if (saved) {
        const filters: TemplatesListFilters = JSON.parse(saved);
        this.searchTerm = filters.searchTerm;
        this.sortBy = filters.sortBy || 'ASC';
        this.orderBy = filters.orderBy || 'name';
        this.current_page = filters.currentPage || 0;
        this.pageSize = filters.pageSize || 10;

        // Atualiza o search service para sincronizar com o header
        // e abre o campo de busca se houver termo
        if (this.searchTerm) {
          this.searchService.setSearchTermImmediate(this.searchTerm, true);
        }

        // Limpa os filtros salvos após carregar
        localStorage.removeItem(TEMPLATES_FILTERS_KEY);
      }
    } catch (e) {
      console.warn('Erro ao carregar filtros de templates:', e);
    }
  }
}
